import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Avatar,
  IconButton,
} from '@mui/material';
import LinkedIn from '@mui/icons-material/LinkedIn';
import GitHub from '@mui/icons-material/GitHub';
import profileImage from '../assets/A.jpg';
import bannerImage from '../assets/df7501b4694ce8a528839b8361d59ce1.jpg';

function Hero() {
  const { t, i18n } = useTranslation();

  return (
    <Box
      id="hero"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 12, md: 14 },
        pb: 8,
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100vw',
      }}
    >
      {/* Banner Background - Full height */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${bannerImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(10, 10, 10, 0.3) 0%, rgba(10, 10, 10, 0.6) 50%, rgba(10, 10, 10, 0.85) 100%)',
          },
        }}
      />


      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 3, md: 5 } }}>
          {/* Profile Picture - Modernisé */}
          <Box
            sx={{
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: { xs: 200, md: 260 },
                height: { xs: 200, md: 260 },
                borderRadius: '50%',
                border: '3px solid rgba(255, 107, 107, 0.4)',
                padding: '5px',
                position: 'relative',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'rgba(255, 107, 107, 0.6)',
                },
              }}
            >
              <Avatar
                src={profileImage}
                alt="Michaël Cadoul"
                sx={{
                  width: '100%',
                  height: '100%',
                  border: '2px solid rgba(15, 20, 25, 0.8)',
                }}
              />
            </Box>
          </Box>

          {/* Text Content */}
          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography
              variant="body2"
              sx={{
                color: '#ff6b6b',
                mb: 1.5,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
                fontWeight: 700,
              }}
            >
              {t('hero.greeting')}
            </Typography>

          <Typography
            variant="h1"
            sx={{
              fontSize: i18n.language === 'ja' 
                ? { xs: '2.2rem', sm: '3.5rem', md: '4.5rem', lg: '5.2rem' }
                : { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
              fontWeight: 900,
              color: '#ffffff',
              mb: 1.5,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              textShadow: '0 2px 20px rgba(0, 0, 0, 0.5)',
              wordBreak: 'keep-all',
              overflowWrap: 'break-word',
            }}
          >
            {t('hero.name')}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.25rem', md: '1.75rem' },
              fontWeight: 600,
              color: '#d4d4d4',
              mb: 1.5,
              lineHeight: 1.3,
            }}
          >
            {t('hero.title')}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#a3a3a3',
              fontSize: { xs: '0.95rem', md: '1.05rem' },
              maxWidth: 700,
              lineHeight: 1.6,
              mb: 3,
              fontWeight: 400,
            }}
          >
            {t('hero.subtitle')}
          </Typography>

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 1.5, sm: 2 },
              mb: { xs: 3, md: 4 },
              background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.2), rgba(78, 205, 196, 0.15))',
              border: '2px solid rgba(255, 107, 107, 0.4)',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(255, 107, 107, 0.2)',
              transition: 'all 0.3s ease',
              maxWidth: '100%',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 30px rgba(255, 107, 107, 0.3)',
              },
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff6b6b, #4ecdc4)',
                animation: 'pulse 2s ease-in-out infinite',
                boxShadow: '0 0 10px rgba(255, 107, 107, 0.6)',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#ffffff',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                fontWeight: 700,
                letterSpacing: '0.05em',
              }}
            >
              {t('hero.visa')}
            </Typography>
          </Box>

            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              alignItems="center"
              sx={{ 
                flexWrap: 'wrap',
                maxWidth: '100%',
                width: '100%'
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                href="#projects"
                sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  px: { xs: 3, sm: 4 },
                  py: { xs: 1.2, sm: 1.5 },
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '300px', sm: 'none' },
                }}
              >
                {t('hero.viewProjects')}
              </Button>
              
              <Stack 
                direction="row" 
                spacing={1.5} 
                sx={{ 
                  pt: { xs: 1, sm: 0 },
                  flexWrap: 'wrap'
                }}
              >
                <IconButton
                  href="https://www.linkedin.com/in/michaël-cadoul-7a5a60178/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#c9d1d9',
                    border: '2px solid rgba(255, 107, 107, 0.3)',
                    borderRadius: '10px',
                    padding: '10px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#ff6b6b',
                      color: '#ff6b6b',
                      backgroundColor: 'rgba(255, 107, 107, 0.1)',
                    },
                  }}
                >
                  <LinkedIn />
                </IconButton>
                <IconButton
                  href="https://github.com/xenom34/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: '#c9d1d9',
                    border: '2px solid rgba(78, 205, 196, 0.3)',
                    borderRadius: '10px',
                    padding: '10px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#4ecdc4',
                      color: '#4ecdc4',
                      backgroundColor: 'rgba(78, 205, 196, 0.1)',
                    },
                  }}
                >
                  <GitHub />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Hero;
